import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'
import { useState, useEffect, useRef, useContext } from 'react'
import _isEmpty from 'lodash/isEmpty'
import {
  formatSelectedFiltersWithCounselingType,
  getSelectedFiltersWithCounselingTypeFromQueryParams,
} from 'app/ProviderSearch/utils'
import { PAGINATION_LIMIT } from 'constants/api'
import { ProviderFilterTypes } from 'dataSource/ProviderSearch'
import CounselingFilter from './CounselingFilter'
import AppointmentModeFilter from './AppointmentModeFilter'
import { modifyURLQuery } from 'utils/common'
import { MobileFilterModalContext } from 'context/ProviderSearch/MobileFilterModalProvider'
import PillMultiselectDropdownFilter from './PillMultiselectDropdownFilter'
import { RETURN_FILTER_KEY_MAPPING } from 'app/ProviderSearch/constants'
import SortFilter from 'app/ProviderSearch/ProviderFilter/SortFilter'
import useBreakpoint from 'hooks/useBreakPoint'
import filterModalStyles from 'app/ProviderSearch/MobileFilterModal/index.module.scss'
import useThriveAuth from 'hooks/useThriveAuth'
import useGetUserTreatment from 'hooks/treatments/useGetUserTreatment'
import { TreatmentFlags, treatmentFlagValues } from 'constants/treatments'

const MobileFilterModal = dynamic(
  () => import('app/ProviderSearch/MobileFilterModal')
)

type ProviderFilterProps = {
  groupedFilters: GroupedProviderFilter
  // Show the sort filter based on this.
  isInPersonSelected: boolean
}

const ProviderFilter: React.FC<ProviderFilterProps> = ({
  groupedFilters,
  isInPersonSelected,
}) => {
  const router = useRouter()
  const { query } = router
  const initialMount = useRef(true)
  const filterUpdate = useRef(true)
  const { isSupportOrThirdPartyUser, isSupportUser } = useThriveAuth()

  const {
    showMobileFilterModal,
    setShowMobileFilterModal,
    clearFilterToExpand,
    filterToExpand,
  } = useContext(MobileFilterModalContext)
  const { isMobileScreen, isLargeSize } = useBreakpoint()
  const [selectedFilter, setSelectedFilter] = useState<GroupedProviderFilter>(
    {}
  )

  useEffect(() => {
    // updates the selected filter from the query only once when the component is mounted and query param is present
    // or if there is a query update without performing remove or apply action

    const selectedFilterFromQuery = getSelectedFiltersWithCounselingTypeFromQueryParams(
      groupedFilters,
      query as NodeJS.Dict<string[]>
    )

    if (!_isEmpty(query) && (initialMount.current || filterUpdate.current)) {
      setSelectedFilter(selectedFilterFromQuery)
      initialMount.current = false
    } else if (!_isEmpty(selectedFilter) && _isEmpty(selectedFilterFromQuery)) {
      setSelectedFilter({})
    }

    filterUpdate.current = true
  }, [query])

  useEffect(() => {
    if (!(isMobileScreen || isLargeSize) && showMobileFilterModal) {
      setShowMobileFilterModal(false)
    }
  }, [isMobileScreen, isLargeSize, showMobileFilterModal])

  const applyFilters = (
    groupedFilters: GroupedProviderFilter,
    additionalFilter?: Record<string, unknown>,
    removeExistingFilters?: boolean
  ) => {
    filterUpdate.current = false
    let formattedFilters = formatSelectedFiltersWithCounselingType(
      groupedFilters
    )
    const { partnershipCode } = query ?? {}

    if (additionalFilter) {
      formattedFilters = {
        ...formattedFilters,
        ...additionalFilter,
      }
    }

    modifyURLQuery(
      router,
      {
        ...formattedFilters,
        limit: PAGINATION_LIMIT.toString(),
        offset: '0',
        ...(!!partnershipCode && { partnershipCode }),
      },
      !!removeExistingFilters
    )
  }

  const onHide = () => {
    // Add custom class for slide-down animation
    document
      .querySelector('.modal-content')
      .classList.add(filterModalStyles.modal_slidedown)

    clearFilterToExpand()

    setTimeout(() => {
      setShowMobileFilterModal(false)
    }, 200)
  }

  const isTimeOfDayFilterFeatureOn =
    useGetUserTreatment(TreatmentFlags.SHOW_TIME_OF_DAY_FILTER) ===
    treatmentFlagValues.ON

  return (
    <>
      <CounselingFilter
        allCounselingTypes={groupedFilters[ProviderFilterTypes.COUNSELING_TYPE]}
      />

      <AppointmentModeFilter
        allAppointmentModes={
          groupedFilters[ProviderFilterTypes.AVAILABILITY_TYPE]
        }
      />
      {isSupportUser && isTimeOfDayFilterFeatureOn && (
        <PillMultiselectDropdownFilter
          filterList={groupedFilters[ProviderFilterTypes.TIME_OF_DAY]}
          filterType={ProviderFilterTypes.TIME_OF_DAY}
          filterKey={RETURN_FILTER_KEY_MAPPING.time_of_day}
          placeholder="Time of day"
          filterTypeText="time of day"
          showListHeaders={false}
        />
      )}
      <PillMultiselectDropdownFilter
        filterList={groupedFilters[ProviderFilterTypes.GENDER]}
        filterType={ProviderFilterTypes.GENDER}
        filterKey={RETURN_FILTER_KEY_MAPPING.gender}
        placeholder="Clinician gender"
        filterTypeText="clinician genders"
        showListHeaders={false}
      />
      <PillMultiselectDropdownFilter
        filterList={groupedFilters[ProviderFilterTypes.CLIENT_AGE_GROUP]}
        filterType={ProviderFilterTypes.CLIENT_AGE_GROUP}
        filterKey={RETURN_FILTER_KEY_MAPPING.client_age_group}
        placeholder="Client age"
        filterTypeText="client ages"
        showListHeaders={false}
        hideFilterSearchInput
      />
      <PillMultiselectDropdownFilter
        filterList={groupedFilters[ProviderFilterTypes.LANGUAGE]}
        filterType={ProviderFilterTypes.LANGUAGE}
        filterKey={RETURN_FILTER_KEY_MAPPING.language}
        placeholder="Language"
        filterTypeText="languages"
      />
      <PillMultiselectDropdownFilter
        filterList={groupedFilters[ProviderFilterTypes.COUNSELING_APPROACH]}
        filterType={ProviderFilterTypes.COUNSELING_APPROACH}
        filterKey={RETURN_FILTER_KEY_MAPPING.counseling_approach}
        placeholder="Counseling approach"
        filterTypeText="counseling approaches"
      />

      {(isInPersonSelected || isSupportOrThirdPartyUser) && <SortFilter />}

      {(isMobileScreen || isLargeSize) && (
        <MobileFilterModal
          showFilterModal={showMobileFilterModal}
          isInPersonSelected={isInPersonSelected}
          filterToExpand={filterToExpand}
          selectedFilter={selectedFilter}
          setSelectedFilter={(
            groupedFilters: GroupedProviderFilter,
            additionalFilter?: Record<string, unknown>
          ) => {
            applyFilters(groupedFilters, additionalFilter, true)
            setSelectedFilter(groupedFilters)
          }}
          groupedFilters={groupedFilters}
          onHide={onHide}
        />
      )}
    </>
  )
}

export default ProviderFilter
